/* =====================================================================
 * SyncQueue — 네트워크 불안정 대응 동기화 큐
 *
 * FIFO 순서로 작업을 처리하며, 실패 시 지수 백오프 재시도를 수행합니다.
 * 오프라인 상태에서는 큐를 일시정지하고, 온라인 복구 시 자동 재개합니다.
 * 같은 key를 가진 작업은 대기 중인 기존 작업을 최신으로 교체(dedup)합니다.
 * ===================================================================== */

export interface SyncOp {
  /** 중복 제거 키 (e.g., 'delete:item123'). 같은 키의 새 작업은 대기열 내 기존 작업을 대체 */
  key: string;
  /** 실행할 비동기 함수 (API 호출) — throw 하면 실패로 간주 */
  execute: () => Promise<void>;
  /** 성공 시 콜백 */
  onSuccess?: () => void;
  /** 최종 실패 시 콜백 (모든 재시도 소진 후) */
  onFailure?: () => void;
}

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 15_000;

class SyncQueue {
  private queue: SyncOp[] = [];
  private processing = false;
  private _online: boolean;

  constructor() {
    this._online = typeof navigator !== 'undefined' ? navigator.onLine : true;

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this._online = true;
        this.flush();
      });
      window.addEventListener('offline', () => {
        this._online = false;
      });
    }
  }

  get online() {
    return this._online;
  }
  get pending() {
    return this.queue.length;
  }

  /**
   * 작업을 큐에 추가합니다.
   * 같은 key를 가진 대기 중인 작업이 있으면 최신으로 교체합니다.
   */
  enqueue(op: SyncOp) {
    const idx = this.queue.findIndex((o) => o.key === op.key);
    if (idx !== -1) {
      this.queue[idx] = op;
    } else {
      this.queue.push(op);
    }
    this.flush();
  }

  /* ---------------------------------------------------------------- */

  private async flush() {
    if (this.processing || !this._online || this.queue.length === 0) return;
    this.processing = true;

    while (this.queue.length > 0 && this._online) {
      const op = this.queue[0];
      let succeeded = false;

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          await op.execute();
          succeeded = true;
          break;
        } catch (err) {
          console.warn(
            `[SyncQueue] ${op.key} 실패 (시도 ${attempt + 1}/${MAX_RETRIES})`,
            err,
          );
          if (!this._online) break;
          if (attempt < MAX_RETRIES - 1) {
            const delay = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
            await this.sleep(delay);
          }
        }
      }

      this.queue.shift();
      if (succeeded) {
        op.onSuccess?.();
      } else {
        op.onFailure?.();
      }
    }

    this.processing = false;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/** 앱 전역 싱글턴 인스턴스 */
export const syncQueue = new SyncQueue();
